import { CancelSurveyDetailCode, CancelSurveyReasonCode } from '@griply/types';
import {
  CHURN_SURVEY_OTHER_PLACEHOLDER_KEY,
  ChurnSurveyContext,
  getChurnSurveyDetailOptions,
  getChurnSurveyFollowUpForReason,
  getChurnSurveyFollowUpQuestionKey,
  getChurnSurveyOpenPlaceholderKey,
  getChurnSurveyOpenQuestionKey,
  getChurnSurveyOtherDetailCode,
  getChurnSurveyReasonOptions,
  getChurnSurveyReasonQuestionKey,
  isChurnSurveyDetailTextRequired,
  isChurnSurveyFollowUpMultiSelect,
  isChurnSurveyOpenAnswerRequired,
  isChurnSurveyOtherDetailCode,
  isChurnSurveyTextAnswerComplete,
} from '@griply/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SurveySelectOption } from 'shared/types/survey-select-option';

export enum SurveyStep {
  Reason = 'reason',
  FollowUp = 'follow-up',
  DetailOther = 'detail-other',
  Open = 'open',
}

export type SurveyAnswers = {
  reason_code: CancelSurveyReasonCode;
  detail_code?: CancelSurveyDetailCode;
  detail_codes?: CancelSurveyDetailCode[];
  detail_text?: string;
  comment?: string;
};

export type SurveyDetailSelection = {
  reason_code: CancelSurveyReasonCode;
  detail_code: CancelSurveyDetailCode;
  detail_text?: string;
};

export type UseSurveyStepsOptions = {
  context: ChurnSurveyContext;
  submitLabel: string;
  reasonSubtitle?: string;
  isSubmitting?: boolean;
  onStarted: () => void;
  onReasonSelected: (reason: CancelSurveyReasonCode) => void;
  onDetailSelected: (selection: SurveyDetailSelection) => void;
  onSubmit: (answers: SurveyAnswers) => void;
  onExit: () => void;
};

// The step machine both churn surveys share, returned as per-step view models.
// Which reasons are offered comes from `context`; the surveys differ only in
// their tracking events, their final action, and a few labels — all injected.
export const useSurveySteps = ({
  context,
  submitLabel,
  reasonSubtitle,
  isSubmitting,
  onStarted,
  onReasonSelected,
  onDetailSelected,
  onSubmit,
  onExit,
}: UseSurveyStepsOptions) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<SurveyStep>(SurveyStep.Reason);
  const [reason, setReason] = useState<CancelSurveyReasonCode | null>(null);
  const [detail, setDetail] = useState<CancelSurveyDetailCode | null>(null);
  const [selectedDetails, setSelectedDetails] = useState<
    CancelSurveyDetailCode[]
  >([]);
  const [detailText, setDetailText] = useState('');
  const [comment, setComment] = useState('');

  const followUp = reason ? getChurnSurveyFollowUpForReason(reason) : null;
  const isMulti = !!followUp && isChurnSurveyFollowUpMultiSelect(followUp);

  const isOpenRequired = isChurnSurveyOpenAnswerRequired(context, reason);
  const canSubmit = !isOpenRequired || isChurnSurveyTextAnswerComplete(comment);
  const canContinueDetailOther =
    !isChurnSurveyDetailTextRequired(context) ||
    isChurnSurveyTextAnswerComplete(detailText);

  const selectReason = useCallback(
    (code: CancelSurveyReasonCode) => {
      setReason(code);
      setDetail(null);
      setSelectedDetails([]);
      setDetailText('');
      onReasonSelected(code);
      setStep(
        getChurnSurveyFollowUpForReason(code)
          ? SurveyStep.FollowUp
          : SurveyStep.Open,
      );
    },
    [onReasonSelected],
  );

  const selectDetail = useCallback(
    (code: CancelSurveyDetailCode) => {
      if (isChurnSurveyOtherDetailCode(code)) {
        setStep(SurveyStep.DetailOther);
        return;
      }
      setDetail(code);
      onDetailSelected({
        reason_code: reason ?? CancelSurveyReasonCode.Other,
        detail_code: code,
      });
      setStep(SurveyStep.Open);
    },
    [onDetailSelected, reason],
  );

  const toggleDetail = useCallback((code: CancelSurveyDetailCode) => {
    if (isChurnSurveyOtherDetailCode(code)) {
      setSelectedDetails([]);
      setStep(SurveyStep.DetailOther);
      return;
    }
    setSelectedDetails((prev) =>
      prev.includes(code)
        ? prev.filter((item) => item !== code)
        : [...prev, code],
    );
  }, []);

  const continueMulti = useCallback(() => {
    if (!selectedDetails.length) {
      return;
    }
    selectedDetails.forEach((code) =>
      onDetailSelected({
        reason_code: reason ?? CancelSurveyReasonCode.Other,
        detail_code: code,
      }),
    );
    setStep(SurveyStep.Open);
  }, [onDetailSelected, reason, selectedDetails]);

  const continueDetailOther = useCallback(() => {
    if (!followUp || !canContinueDetailOther) {
      return;
    }
    const otherCode = getChurnSurveyOtherDetailCode(followUp);
    setDetail(otherCode);
    onDetailSelected({
      reason_code: reason ?? CancelSurveyReasonCode.Other,
      detail_code: otherCode,
      detail_text: detailText.trim() || undefined,
    });
    setStep(SurveyStep.Open);
  }, [canContinueDetailOther, detailText, followUp, onDetailSelected, reason]);

  const detailCodes = useMemo(
    () =>
      selectedDetails.length ? selectedDetails : detail ? [detail] : undefined,
    [detail, selectedDetails],
  );

  const submit = useCallback(() => {
    onSubmit({
      reason_code: reason ?? CancelSurveyReasonCode.Other,
      detail_code: detail ?? undefined,
      detail_codes: detailCodes,
      detail_text: detailText.trim() || undefined,
      comment: comment.trim() || undefined,
    });
  }, [comment, detail, detailCodes, detailText, onSubmit, reason]);

  const onBack = useCallback(() => {
    if (step === SurveyStep.Open) {
      setStep(followUp ? SurveyStep.FollowUp : SurveyStep.Reason);
      return;
    }
    if (step === SurveyStep.DetailOther) {
      setStep(SurveyStep.FollowUp);
      return;
    }
    if (step === SurveyStep.FollowUp) {
      setStep(SurveyStep.Reason);
      return;
    }
    onExit();
  }, [followUp, onExit, step]);

  const reasonOptions = useMemo<SurveySelectOption[]>(
    () =>
      getChurnSurveyReasonOptions(context).map((option) => ({
        key: option.code,
        label: t(option.labelKey),
        selected: false,
        onClick: () => selectReason(option.code),
      })),
    [context, selectReason, t],
  );

  const followUpOptions = useMemo<SurveySelectOption[]>(
    () =>
      followUp
        ? getChurnSurveyDetailOptions(followUp).map((option) => ({
            key: option.code,
            label: t(option.labelKey),
            selected: isMulti && selectedDetails.includes(option.code),
            onClick: () =>
              isMulti ? toggleDetail(option.code) : selectDetail(option.code),
          }))
        : [],
    [followUp, isMulti, selectDetail, selectedDetails, t, toggleDetail],
  );

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    onStarted();
  }, [onStarted]);

  return {
    step,
    onBack,
    backLabel:
      step === SurveyStep.Reason ? t('general.cancel') : t('general.back'),
    continueLabel: t('cancel-subscription-survey.continue'),
    reason: {
      title: t(getChurnSurveyReasonQuestionKey(context)),
      subtitle: reasonSubtitle,
      options: reasonOptions,
    },
    followUp: followUp
      ? {
          title: t(getChurnSurveyFollowUpQuestionKey(followUp)),
          subtitle: isMulti
            ? t('cancel-subscription-survey.multi-select-hint')
            : undefined,
          isMulti,
          options: followUpOptions,
          onContinue: continueMulti,
          canContinue: !!selectedDetails.length,
        }
      : null,
    detailOther: {
      title: followUp ? t(getChurnSurveyFollowUpQuestionKey(followUp)) : '',
      placeholder: t(CHURN_SURVEY_OTHER_PLACEHOLDER_KEY),
      value: detailText,
      onChange: setDetailText,
      onContinue: continueDetailOther,
      canContinue: canContinueDetailOther,
    },
    open: {
      title: t(getChurnSurveyOpenQuestionKey(context, reason)),
      placeholder: t(getChurnSurveyOpenPlaceholderKey(context, reason)),
      value: comment,
      onChange: setComment,
      onContinue: submit,
      continueLabel: submitLabel,
      canContinue: canSubmit,
      loading: isSubmitting,
    },
  };
};
